import type { AuthUser } from "aws-amplify/auth";
import { fetchAuthSession, signOut } from "aws-amplify/auth";
import type { RootState } from "./index";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";
import { whoamiEndpoints } from "@realtorSitesClient/clients/vowApi/whoami/whoami";
import {
  cacheUserAssets,
  clearCachedUserAssets,
} from "@realtorSitesClient/serviceWorker/triggers";

export type Organization = any;
export type OrganizationUser = any;
export type User = {
  id: string;
  sub: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string | null;
  organizationUsers?: OrganizationUser[];
};

export type AuthUserWithCognito = User & {
  organizationUsers: Array<
    OrganizationUser & {
      organization: Organization;
    }
  >;
  cognitoUser: AuthUser;
};

interface AuthState {
  user: AuthUserWithCognito | null;
  selectedOrganization?:
    | (OrganizationUser & {
        organization: Organization;
      })
    | null;
  initialized: boolean;
}

const initialState: AuthState = {
  user: null,
  selectedOrganization: null,
  initialized: false,
};

export const logout = createAsyncThunk("users/logout", async () => {
  return signOut().finally(() => {
    Sentry.setUser(null);
    clearCachedUserAssets();
  });
});

export const login = createAsyncThunk(
  "users/login",
  async (
    user: AuthUser,
    { dispatch },
  ): Promise<{
    user: AuthUserWithCognito | null;
    selectedOrganization:
      | (OrganizationUser & {
          organization: Organization;
        })
      | null;
  }> => {
    Sentry.setUser({
      sub: user.username,
    });

    const whoamiResult = await dispatch(
      whoamiEndpoints.whoami.initiate(void 0, {
        forceRefetch: true,
      }),
    );

    if (whoamiResult.error) {
      Sentry.captureException(Error("Failed to fetch user info"));
      dispatch(logout());
      return { user: null, selectedOrganization: null };
    }
    if (!whoamiResult.data) {
      Sentry.captureException(Error("No user info returned"));
      dispatch(logout());
      return { user: null, selectedOrganization: null };
    }

    fetchAuthSession()
      .then(({ tokens }) => tokens?.accessToken.toString())
      .then(cacheUserAssets)
      .catch(() => undefined);

    return {
      user: {
        ...whoamiResult.data.user,
        cognitoUser: user,
      },
      selectedOrganization:
        whoamiResult.data?.user?.organizationUsers?.[0] || null,
    };
  },
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(logout.fulfilled, (state) => {
      state.user = null;
      state.selectedOrganization = null;
    });
    builder.addCase(login.fulfilled, (state, action) => {
      state.user = action.payload.user;
      state.selectedOrganization = action.payload.selectedOrganization;
      state.initialized = true;
    });
  },
});

export const selectUser = (state: RootState): AuthUserWithCognito | null =>
  state.auth.user;
export const selectUserType = (_state: RootState): string | null => null;
export const selectCurrentOrganization = (
  state: RootState,
): (OrganizationUser & { organization: Organization }) | null =>
  state.auth.selectedOrganization || null;
export const selectIsLoggedIn = (state: RootState) => state.auth.user !== null;

export default authSlice.reducer;
